import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

import workout from "../../images/workout.png";
import demo from "../../images/demo.png";
import video from "../../images/video.svg";
import plus from "../../images/plus.svg";
import upload from "../../images/upload.svg";
import greenClock from "../../images/greenClock.svg";

export const AthleteSpeedWorkoutSprints = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { athlete } = useAthleteAuthContext();
  const { product_id } = useParams();

  // ✅ Receive all data via state
  const {
    product_basic,
    weekNumber,
    dayName,
    sprints,
    training_focus,
    training_phase,
  } = location.state || {};

  // console.log(sprints);

  const [speedWorkout, setSpeedWorkout] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [athleteProfile, setAthleteProfile] = useState(null);

  useEffect(() => {
    const fetchAthleteProfile = async () => {
      try {
        const response = await fetch(`${API_URL.GET_ATHLETE_BY_EMAIL}/${athlete.email}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${athlete.token}` },
        });
        const data = await response.json();
        console.log(data);
        if (response.ok) setAthleteProfile(data);
      } catch (err) {
        console.error("Failed to fetch athlete profile", err);
      }
    };
  
    if (athlete?.email) fetchAthleteProfile();
  }, [athlete.email, athlete.token]);


  const getGoalTime = (pace) => {
    if (!athleteProfile) return 0;
    if (pace === 100) return parseFloat(athleteProfile.pr_100m) || 0;
    if (pace === 200) return parseFloat(athleteProfile.pr_200m) || 0;
    if (pace === 400) return parseFloat(athleteProfile.pr_400m) || 0;
    return 0;
  };

  const computeTargetTime = (pace, intensity, distance) => {
    const goal_time = getGoalTime(pace);
    if (!goal_time || !pace || !intensity || !distance) return "N/A";
    return (
      Math.ceil(
        (goal_time / (pace / 100) / (intensity / 100) / 100) * distance * 10
      ) / 10
    ).toFixed(1);
  };


  //console.log(sprints);

  //console.log(athlete);


  // Compute total distance
  const totalDistance = sprints?.reduce(
    (sum, sprint) =>
      sum + (sprint.sets || 0) * (sprint.reps || 0) * (sprint.distance || 0),
    0,
  );

  // Navigation to warmup routine
  const handleStaticWarmupClick = () => {
    navigate(
      `/athlete/speed-product/warmup-routine-phases/${product_basic._id}`,
      { state: { product_basic } },
    );
  };

  const handleAccordionClick = (index) =>
    setSpeedWorkout(speedWorkout === index ? null : index);

  // Send a new message from Sprint page
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() && attachments.length === 0) return;

    // Build automatic context header
    const workoutContext = `
  [Workout Context]
  Product: ${product_basic?.title || "N/A"}
  Week: ${weekNumber}
  Day: ${dayName.charAt(0).toUpperCase() + dayName.slice(1)}
  ${
    speedWorkout !== null && sprints[speedWorkout]?.type !== "Rest"
      ? `Sprint: Sets ${sprints[speedWorkout].sets} × Reps ${sprints[speedWorkout].reps} × Distance ${sprints[speedWorkout].distance}m, Intensity ${sprints[speedWorkout].intensity}`
      : "Sprint: General Workout Message"
  }
  
  User Message:
  ${message}
    `;

    const formData = new FormData();
    formData.append("sender_id", athlete.id);
    formData.append("message", workoutContext.trim());
    formData.append("sender_type", "athlete");
    formData.append("receiver_type", "amax");
    formData.append("receiver_id", "amax");

    attachments.forEach((file) => formData.append("attachments", file));

    setSending(true);

    try {
      const res = await fetch(API_URL.SEND_ATHLETE_MESSAGE_TO_AMAX, {
        method: "POST",
        headers: { Authorization: `Bearer ${athlete.token}` },
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        setMessage("");
        setAttachments([]);
        setShowMessagePopup(false);
        setSuccessMsg("Message sent successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        alert(data.error || "Failed to send message");
      }
    } catch (err) {
      console.error(err);
      alert("Error sending message");
    } finally {
      setSending(false);
    }
  };

  const handleTTPopupClick = () => {
    setShowPopup(true);
  };

  const handleBackClick = () => {
    navigate(`/athlete/speed-product/speed-workout/${product_id}`, {
      state: {
        speedTrainingTemplate: product_basic.speed_training_template,
        product_basic: product_basic, // for back navigation
      },
    });
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-[0px] z-10 bg-white">
            {/* Product Title Header */}
            <div className="w-full max-w-2xl mx-auto px-5">
              <div className="py-3 mb-3 flex flex-row justify-center items-center gap-10 bg-white border-b border-black/30 ">
                {/* LEFT: back button */}
                <button onClick={handleBackClick} className="pl-3 pr-3 py-2">
                  <ArrowLWhite />
                </button>

                {/* CENTER: title */}
                <p className="text-black text-xl text-center line-clamp-2 leading-tight px-2">
                  {product_basic?.title || "Workout"}
                </p>

                {/* RIGHT: invisible spacer to balance layout */}
                <div className="pr-3 opacity-0">
                  <ArrowLWhite />
                </div>
              </div>
            </div>

            <div className="w-full max-w-2xl mx-auto">
              <div className="text-white pt-3 pb-1">
                {/* Tabs */}
                <div className="px-3 xs:px-5">
                  <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4">
                    <button
                      onClick={handleStaticWarmupClick}
                      className="text-lightSmock text-sm xs:text-base"
                    >
                      Warmup
                    </button>
                    <button className="text-black text-sm xs:text-base">
                      Workout
                    </button>
                  </div>
                </div>

                <div className="w-full text-center relative"></div>

                <div
                  className={`${speedWorkout !== null ? "hidden" : "block"} relative px-3 xs:px-5`}
                >
                  <div className="flex justify-between items-center text-white w-full bg-black rounded-xl p-3 my-3">
                    <div className="flex items-center gap-2">
                      <p className="text-base xs:text-xl font-medium">{`W${weekNumber}`}</p>
                      <p className="text-base xs:text-xl font-medium">|</p>
                      <p className="text-base xs:text-xl font-medium capitalize">
                        {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                      </p>
                    </div>
                    <p className="text-xs xs:text-sm font-medium">
                      {totalDistance} m
                    </p>
                  </div>
                </div>

                <div
                  className={`${speedWorkout !== null ? "block" : "hidden"} px-3 xs:px-5 my-3`}
                >
                  <div className="flex justify-between items-center text-white w-full bg-black rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <p className="text-base xs:text-xl font-medium">{`W${weekNumber}`}</p>
                      <p className="text-base xs:text-xl font-medium">|</p>
                      <p className="text-base xs:text-xl font-medium">
                        {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                      </p>
                    </div>
                    <p className="text-xs xs:text-sm font-medium">
                      {totalDistance} m
                    </p>
                  </div>
                </div>

                {/* label header */}
                <div className="px-3 xs:px-5 mb-3">
                  <div className="bg-black rounded-xl py-3 px-3 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
                    <div className="w-[14%] px-2 xs:px-2 flex justify-center items-center">
                      <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                        Sets
                      </p>
                    </div>
                    <div className="w-[15%] px-2 xs:px-2 flex justify-center items-center">
                      <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                        Reps
                      </p>
                    </div>
                    <div className="w-[24%] px-2 xs:px-2 flex justify-center items-center">
                      <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                        Distance
                      </p>
                    </div>
                    <div className="w-[24%] px-2 xs:px-2 flex justify-center items-center">
                      <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                        Intensity
                      </p>
                    </div>
                    <div className="w-[15%] px-2 xs:px-2 flex justify-center items-center">
                      <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                        T/T
                      </p>
                    </div>
                    <div className="w-[8%] opacity-0">
                      <div className="w-5 h-5"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full max-w-2xl mx-auto">
            {showPopup && (
              <PopupMessage
                message="This is only available to Amax coached athletes!"
                onClose={() => setShowPopup(false)}
              />
            )}

            {showMessagePopup && (
              <MessageCoachPopup
                onClose={() => setShowMessagePopup(false)}
                message={message}
                setMessage={setMessage}
                onSend={handleSendMessage}
                sending={sending}
              />
            )}

            {/* Sprint Workout List */}
            <div className="flex flex-col gap-5 px-3 xs:px-5">
              {sprints?.map((item, index) => (
                <div
                  key={index}
                  className="bg-transparent py-3 px-3 rounded-xl border border-black/30 bg-white"
                >
                  {/* --- Exercise Name + Thumbnail (if exists) --- */}
                  {item.exercise_id?.title && (
                    <div
                      className="flex justify-between items-center gap-3 mb-2 py-1 pl-3 border-l-2 border-darkYellow cursor-pointer"
                      onClick={() => handleAccordionClick(index)}
                    >
                      <p className="text-black text-base font-bold">
                        {item.exercise_id.title}
                      </p>
                    </div>
                  )}

                  {/* --- Sprint Row for normal sprint or rest --- */}
                  {item.type === "Rest" ? (
                    // Rest row
                    <div className="flex flex-row justify-center items-center text-center text-black">
                      <div className="space-y-1">
                        <label className="text-lightSmock text-xs uppercase">
                          Rest
                        </label>
                        <p className="text-black">{item.rest_notes}</p>
                      </div>
                    </div>
                  ) : (
                    // Normal sprint row with accordion
                    <div
                      className="flex flex-row justify-between items-stretch text-center cursor-pointer text-black max-xs:-mx-2"
                      onClick={() => handleAccordionClick(index)}
                    >
                      <div className="max-xs:h-auto w-[15%] flex justify-center items-center px-1 border-r border-black">
                        <p className="max-xs:text-xs font-light">{item.sets}</p>
                      </div>
                      <div className="max-xs:h-auto w-[15%] flex justify-center items-center px-1 border-r border-black">
                        <p className="max-xs:text-xs font-light">{item.reps}</p>
                      </div>
                      <div className="max-xs:h-auto w-[24%] flex justify-center items-center px-1 border-r border-black">
                        <p className="max-xs:text-xs font-light">
                          {item.distance}
                        </p>
                      </div>
                      <div className="max-xs:h-auto w-[23%] flex justify-center items-center px-1 border-r border-black">
                        <p className="max-xs:text-xs font-light">
                          {item.intensity}
                        </p>
                      </div>
                      <div className="max-xs:h-auto w-[15%] flex justify-center items-center px-1">
                        <span className="relative group block">
                          <p className="max-xs:text-xs font-light"> {computeTargetTime(item.pace, item.intensity, item.distance)}</p>
                         { /* <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden w-max px-2 py-1 whitespace-normal text-xs text-black bg-gray-700 rounded-md opacity-0 group-hover:opacity-100 group-hover:block transition-opacity duration-300">
                            This is only available to Amax coached athletes
                          </span>
                          */}
                        </span>
                      </div>
                      <div
                        onClick={() => handleAccordionClick(index)}
                        className="w-[8%] flex justify-end my-auto"
                      >
                        <div className="h-5 w-5 bg-darkYellow text-black flex justify-center items-center p-1 cursor-pointer">
                          {speedWorkout === index ? (
                            <>
                              <Minus />
                            </>
                          ) : (
                            <>
                              <Plus />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* --- Expanded info only for normal sprint --- */}
                  {item.type !== "Rest" && speedWorkout === index && (
                    <>
                      <div className="text-black text-sm font-medium my-3">
                        <p>
                          <span className="uppercase">Rest: </span>
                          {item.rest}
                        </p>
                        {item.notes && (
                          <p className="whitespace-pre-line">
                            <span className="uppercase">Notes: </span>
                            {item.notes}
                          </p>
                        )}
                      </div>
                      <div className="w-full flex flex-row gap-4 mb-4">
                        <div
                          className="w-1/2 h-[120px] border border-black/30 rounded-2xl overflow-hidden cursor-pointer"
                          onClick={() =>
                            item.exercise_id?.video_url &&
                            setShowVideo(item.exercise_id.video_url)
                          }
                        >
                          {item.exercise_id?.video_url ? (
                            <img
                              src={`https://img.youtube.com/vi/${item.exercise_id.video_url.split("v=")[1]?.split("&")[0]}/hqdefault.jpg`}
                              alt={item.exercise_id.title}
                              className="object-cover h-full w-full"
                            />
                          ) : (
                            <img
                              src={demo}
                              alt="demo"
                              className="object-cover object-[100%_100%]"
                            />
                          )}
                        </div>

                        <div className="w-1/2 h-[120px] border border-black/30 p-4 rounded-2xl overflow-hidden">
                          <img
                            src={video}
                            alt="video"
                            className="object-contain mb-6 cursor-pointer"
                          />
                          <p className="text-black text-sm font-medium mb-3">
                            Upload video
                          </p>
                          <img
                            onClick={handleTTPopupClick}
                            src={upload}
                            alt="upload"
                            className="object-contain cursor-pointer invert"
                          />
                        </div>
                      </div>
                      <div className="max-xs:px-0 p-3 flex justify-between items-center">
                        <p className="text-black text-base xs:text-xl font-medium">
                          Actual Time
                        </p>
                        <img
                          src={greenClock}
                          alt="greenClock"
                          className="object-contain cursor-pointer w-5"
                        />
                      </div>

                      <div className="max-xs:px-0 p-3 pt-0 flex justify-between items-center">
                        <div className="relative w-full border-b border-black/30">
                          <input
                            type="text"
                            className="placeholder:text-[#5D5D5D] placeholder:font-medium text-black text-base font-light bg-transparent w-full outline-none p-2 pr-12"
                            placeholder="Actual time"
                          />
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full size-8 flex justify-center items-center bg-grayish/50 hover:bg-darkYellow transition group">
                            <img
                              onClick={handleTTPopupClick}
                              src={plus}
                              alt="plus"
                              className="w-4 invert"
                            />
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => setShowMessagePopup(true)}
                        className="bg-darkYellow w-full p-3 rounded-full block capitalize text-black text-base xs:text-xl font-medium mt-5"
                      >
                        Message the Coach
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {showVideo && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
              <div className="relative w-full max-w-3xl h-[60vh]">
                <iframe
                  src={showVideo.replace("watch?v=", "embed/")}
                  title="Sprint Video"
                  className="w-full h-full rounded-lg"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <button
                  onClick={() => setShowVideo(null)}
                  className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-50 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const ArrowWhite = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="8"
      viewBox="0 0 14 8"
      fill="none"
    >
      <path
        d="M1 0.999999L7 7L13 1"
        stroke="black"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// BACK ARROW ICON
const ArrowLWhite = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 rotate-180 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M1 13L7 7L1 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const PopupMessage = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-5 rounded shadow-lg text-center">
      <p className="text-black text-sm font-bold">{message}</p>
      <button
        onClick={onClose}
        className="mt-3 px-4 py-2 bg-black text-white rounded hover:opacity-80"
      >
        Close
      </button>
    </div>
  </div>
);

const MessageCoachPopup = ({
  onClose,
  message,
  setMessage,
  onSend,
  sending,
}) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-4 xs:p-6 rounded-lg shadow-lg w-full max-w-md">
      <h2 className="text-black text-lg xs:text-xl font-bold mb-4">
        Message the Coach
      </h2>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows="4"
        className="w-full text-base font-medium border border-gray-300 p-2 rounded focus:outline-none focus:ring focus:ring-darkYellow text-black placeholder-gray-500"
        placeholder="Type your message here..."
      />
      <div className="mt-4 flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 transition"
        >
          Cancel
        </button>
        <button
          onClick={onSend}
          disabled={sending}
          className="px-4 py-2 bg-black text-white rounded hover:bg-black/70 disabled:opacity-50 transition"
        >
          Send
        </button>
      </div>
    </div>
  </div>
);
